import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { UilImageUpload, UilEdit, UilStar } from "@iconscout/react-unicons";
import "./ProductImageUpload.css";
import { resolveUrl } from "../../../utils/storeUtils";

const MAX_IMAGES = 10;

function getCroppedImg(imageSrc, crop, fileName) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const w = image.naturalWidth;
      const h = image.naturalHeight;
      const pixelCrop = {
        x: (crop.x / 100) * w,
        y: (crop.y / 100) * h,
        width: (crop.width / 100) * w,
        height: (crop.height / 100) * h,
      };
      if (pixelCrop.width < 1 || pixelCrop.height < 1) return reject(new Error("Crop too small"));
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas is empty"));
          const ext = fileName?.split(".").pop() || "jpg";
          const name = fileName?.replace(/\.[^.]+$/, "") || "image";
          resolve(new File([blob], `${name}-cropped.${ext}`, { type: blob.type }));
        },
        "image/jpeg",
        0.92
      );
    };
    image.onerror = reject;
  });
}

function centerAspectCrop(mediaWidth, mediaHeight, aspect = 1) {
  return centerCrop(
    makeAspectCrop(
      { unit: "%", width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function ProductImageUpload({ images = [], onChange, error, disabled }) {
  const [cropModal, setCropModal] = useState({ open: false, src: null, file: null, replaceIndex: null });
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [cropAspect, setCropAspect] = useState(1);
  const [imgRef, setImgRef] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const replaceInputRef = useRef(null);
  const replaceIndexRef = useRef(null);
  const originalByIndexRef = useRef({});
  const resetToFullRef = useRef(false);
  const prevImagesLengthRef = useRef(images.length);

  useEffect(() => {
    if (images.length < prevImagesLengthRef.current) {
      originalByIndexRef.current = {};
    }
    prevImagesLengthRef.current = images.length;
  }, [images.length]);

  const onImageLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (resetToFullRef.current) {
      resetToFullRef.current = false;
      const fullCrop = { unit: "%", x: 0, y: 0, width: 100, height: 100 };
      setCrop(fullCrop);
      setCompletedCrop(fullCrop);
    } else {
      setCrop(centerAspectCrop(naturalWidth, naturalHeight, 1));
    }
  }, []);

  const handleFileSelect = (file, replaceIndex = null) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (replaceIndex === null && images.length >= MAX_IMAGES) return;
    const src = URL.createObjectURL(file);
    setCropAspect(1);
    if (typeof replaceIndex === "number") {
      originalByIndexRef.current[replaceIndex] = { type: "file", file };
    }
    setCropModal({ open: true, src, file, replaceIndex, isObjectUrl: true });
    setCompletedCrop(undefined);
  };

  const handleEditCropClick = async (index) => {
    if (disabled) return;
    const img = images[index];
    if (!img) return;
    let src = null;
    if (img instanceof File) {
      src = URL.createObjectURL(img);
    } else if (img?.url) {
      src = resolveUrl(String(img.url || ""));
    }
    if (!src) return;
    if (!originalByIndexRef.current[index]) {
      originalByIndexRef.current[index] =
        img instanceof File ? { type: "file", file: img } : { type: "url", url: img.url };
    }
    setCropAspect(1);
    setCropModal({
      open: true,
      src,
      file: null,
      replaceIndex: index,
      isObjectUrl: img instanceof File,
    });
    setCompletedCrop(undefined);
  };

  const handleReplaceClick = (index) => {
    if (disabled) return;
    replaceIndexRef.current = index;
    replaceInputRef.current?.click();
  };

  const handleReplaceInputChange = (e) => {
    const file = e.target.files?.[0];
    const index = replaceIndexRef.current;
    e.target.value = "";
    replaceIndexRef.current = null;
    if (file && typeof index === "number") handleFileSelect(file, index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleCropComplete = async () => {
    if (!cropModal.src || !completedCrop || !imgRef) return;
    try {
      const fileName = cropModal.file?.name || cropModal.src.split("/").pop()?.split("?")[0] || "image.jpg";
      const croppedFile = await getCroppedImg(cropModal.src, completedCrop, fileName);
      const { replaceIndex } = cropModal;
      if (typeof replaceIndex === "number" && replaceIndex >= 0 && replaceIndex < images.length) {
        const next = [...images];
        next[replaceIndex] = croppedFile;
        onChange(next);
      } else {
        onChange([...images, croppedFile]);
      }
      closeCropModal();
    } catch (err) {
      console.error("Crop failed:", err);
    }
  };

  const handleRemoveImage = (index) => {
    const next = images.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleSetAsMain = (index) => {
    if (index === 0 || disabled) return;
    const next = [...images];
    const [removed] = next.splice(index, 1);
    next.unshift(removed);
    onChange(next);
  };

  const handleDropReorder = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = draggedIndex ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (isNaN(dragIndex) || dragIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    const next = [...images];
    const [removed] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, removed);
    onChange(next);
    setDraggedIndex(null);
  };

  const getPreviewUrl = (img) => {
    if (img instanceof File) return URL.createObjectURL(img);
    const raw = img?.url || img;
    const s = String(raw || "");
    if (!s) return "";
    if (s.startsWith("blob:") || s.startsWith("data:")) return s;
    return resolveUrl(s);
  };

  const closeCropModal = () => {
    if (cropModal.isObjectUrl && cropModal.src) URL.revokeObjectURL(cropModal.src);
    setCropModal({ open: false, src: null, file: null, replaceIndex: null, isObjectUrl: false });
  };

  const handleResetCrop = () => {
    const fullCrop = { unit: "%", x: 0, y: 0, width: 100, height: 100 };
    setCropAspect(undefined);
    setCrop(fullCrop);
    setCompletedCrop(fullCrop);

    if (!cropModal.file) {
      const orig = originalByIndexRef.current[cropModal.replaceIndex];
      if (orig) {
        if (cropModal.isObjectUrl && cropModal.src) URL.revokeObjectURL(cropModal.src);
        resetToFullRef.current = true;
        const newSrc = orig.type === "file"
          ? URL.createObjectURL(orig.file)
          : orig.url;
        setCropModal((prev) => ({
          ...prev,
          src: newSrc,
          isObjectUrl: orig.type === "file",
        }));
      }
    }
  };

  return (
    <div className="product-image-upload">
      <div className="product-image-upload-list">
        {images.map((img, index) => (
          <div
            key={index}
            className={`product-image-upload-item ${draggedIndex === index ? "dragging" : ""}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDropReorder(e, index)}
          >
            <img src={getPreviewUrl(img)} alt="" />
            <span className="product-image-upload-order">{index + 1}</span>
            <div className="product-image-upload-actions">
              {index > 0 && (
                <button
                  type="button"
                  className="product-image-upload-set-main"
                  onClick={(e) => { e.stopPropagation(); handleSetAsMain(index); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={disabled}
                  aria-label="Set as main photo"
                  title="Set as main photo"
                >
                  <UilStar size={14} />
                </button>
              )}
              <button
                type="button"
                className="product-image-upload-edit"
                onClick={(e) => { e.stopPropagation(); handleEditCropClick(index); }}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={disabled}
                aria-label="Edit crop"
                title="Edit crop"
              >
                <UilEdit size={14} />
              </button>
              <button
                type="button"
                className="product-image-upload-replace"
                onClick={(e) => { e.stopPropagation(); handleReplaceClick(index); }}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={disabled}
                aria-label="Change photo"
                title="Change photo"
              >
                <UilImageUpload size={14} />
              </button>
            </div>
            <button
              type="button"
              className="product-image-upload-remove"
              onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Remove image"
            >
              <i className="uil uil-times" />
            </button>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <div
            className="product-image-upload-add"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => { e.stopPropagation(); handleDrop(e); }}
          >
            <UilImageUpload size={32} />
            <span>Add photo</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
              disabled={disabled}
            />
          </div>
        )}
      </div>
      <p className="product-image-upload-hint">
        Drag to reorder. Star: set as main. Edit: re-crop. Change: pick new photo. First image is the main photo. Up to {MAX_IMAGES} images.
      </p>
      {error && <p className="product-image-upload-error">{error}</p>}

      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        className="product-image-upload-replace-input"
        onChange={handleReplaceInputChange}
        aria-hidden
      />
      {cropModal.open && (
        <div className="product-image-crop-modal" onClick={closeCropModal}>
          <div className="product-image-crop-content" onClick={(e) => e.stopPropagation()}>
            <h4>Crop image</h4>
            <div className="product-image-crop-area">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                aspect={cropAspect}
                circularCrop={false}
              >
                <img
                  ref={setImgRef}
                  src={cropModal.src}
                  alt="Crop"
                  onLoad={onImageLoad}
                  style={{ maxHeight: "70vh", maxWidth: "100%" }}
                />
              </ReactCrop>
            </div>
            <div className="product-image-crop-actions">
              <button type="button" className="product-image-crop-reset" onClick={handleResetCrop}>
                Reset Photo
              </button>
              <button type="button" onClick={closeCropModal}>
                Cancel
              </button>
              <button type="button" onClick={handleCropComplete} disabled={!completedCrop}>
                Apply crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Convert images array (File | {url}) to File[] for upload. Fetches URLs and converts to File. */
export async function getImageFilesForUpload(images) {
  const files = [];
  for (const img of images) {
    if (img instanceof File) {
      files.push(img);
    } else if (img?.url) {
      try {
        const fetchUrl = resolveUrl(String(img.url || ""));
        const res = await fetch(fetchUrl);
        const blob = await res.blob();
        const name = String(img.url || "").split("/").pop()?.split("?")[0] || "image.jpg";
        files.push(new File([blob], name, { type: blob.type || "image/jpeg" }));
      } catch (err) {
        console.warn("Could not fetch image for re-upload:", img.url, err);
      }
    }
  }
  return files;
}

export default ProductImageUpload;
